import {
  ExternalServiceType,
  InternalStorageInstructionType,
  SyncToDataDestinationFieldMapping,
  SyncToDataDestinationTarget
} from '@dadohr/shared/domainTypes/experience';
import { Integration } from '@dadohr/shared/domainTypes/integrations';

/**
 * The "Sync to Data Destination" automation action stores ONE `destination`
 * plus an N-row `mappings` array (`{ sourceField, target }`). This matches the
 * backend contract in `functions/experiences/src/domain/syncToDataDestinationEnqueue.ts`,
 * which resolves each `mappings[].sourceField` against the participant and
 * writes it to `mappings[].target` in the single `destination`.
 *
 * These helpers back both the authoring panel and the builder validation.
 */

export type SyncDestinationType = SyncToDataDestinationTarget['type'];

export type SyncDestinationDefinition = {
  type: SyncDestinationType;
  /** Integration that must be active for this destination to be selectable
   * (`null` = no integration gating, e.g. the Dado field destination). */
  integration: Integration | null;
  label: string;
};

export const SYNC_DESTINATIONS: SyncDestinationDefinition[] = [
  {
    type: ExternalServiceType.GOOGLE_SHEET,
    integration: Integration.GOOGLE_SHEETS,
    label: 'Google Sheet'
  },
  {
    type: ExternalServiceType.MS_EXCEL,
    integration: Integration.MICROSOFT,
    label: 'Excel'
  },
  {
    type: ExternalServiceType.ADP_WORKFORCE_NOW,
    integration: Integration.ADP_WORKFORCE_NOW,
    label: 'ADP Workforce Now'
  },
  {
    type: ExternalServiceType.PERSONIO,
    integration: Integration.PERSONIO,
    label: 'Personio'
  },
  {
    type: ExternalServiceType.BAMBOO_HR,
    integration: Integration.BAMBOOHR,
    label: 'BambooHR'
  },
  {
    type: ExternalServiceType.SALESFORCE,
    integration: Integration.SALESFORCE,
    label: 'Salesforce'
  },
  {
    type: InternalStorageInstructionType.USER_PROFILE,
    integration: null,
    label: 'Dado field'
  }
];

export const getSyncDestinationDefinition = (
  type: SyncDestinationType
): SyncDestinationDefinition | undefined =>
  SYNC_DESTINATIONS.find((d) => d.type === type);

/** A fresh, empty destination of the given type. */
export const createEmptyDestination = (
  type: SyncDestinationType
): SyncToDataDestinationTarget => {
  switch (type) {
    case ExternalServiceType.GOOGLE_SHEET:
      return { type: ExternalServiceType.GOOGLE_SHEET, resourceId: '' };
    case ExternalServiceType.MS_EXCEL:
      return { type: ExternalServiceType.MS_EXCEL, resourceId: '' };
    case ExternalServiceType.SALESFORCE:
      return {
        type: ExternalServiceType.SALESFORCE,
        resourceId: '',
        objectName: ''
      };
    case ExternalServiceType.ADP_WORKFORCE_NOW:
      return { type: ExternalServiceType.ADP_WORKFORCE_NOW };
    case ExternalServiceType.BAMBOO_HR:
      return { type: ExternalServiceType.BAMBOO_HR };
    case ExternalServiceType.PERSONIO:
      return { type: ExternalServiceType.PERSONIO };
    case InternalStorageInstructionType.USER_PROFILE:
      return { type: InternalStorageInstructionType.USER_PROFILE };
  }
};

/** True when the destination has everything the backend needs to route it. */
export const isSyncDestinationComplete = (
  d: SyncToDataDestinationTarget
): boolean => {
  switch (d.type) {
    case ExternalServiceType.GOOGLE_SHEET:
    case ExternalServiceType.MS_EXCEL:
      return !!d.resourceId;
    case ExternalServiceType.SALESFORCE:
      return !!d.resourceId && !!d.objectName;
    case ExternalServiceType.ADP_WORKFORCE_NOW:
    case ExternalServiceType.BAMBOO_HR:
    case ExternalServiceType.PERSONIO:
    case InternalStorageInstructionType.USER_PROFILE:
      return true;
  }
};

export const isSyncMappingComplete = (
  m: SyncToDataDestinationFieldMapping
): boolean => !!m.sourceField && !!m.target;

/**
 * Null-safe accessor for the action's `{ destination, mappings }`. Both fields
 * are optional on the stored/denormalized shapes, so this normalizes them to a
 * concrete destination-or-null and an always-array mappings for the panel,
 * summary and denormalizers.
 */
export const readSyncConfig = (action: {
  destination?: SyncToDataDestinationTarget | null;
  mappings?: SyncToDataDestinationFieldMapping[] | null;
}): {
  destination: SyncToDataDestinationTarget | null;
  mappings: SyncToDataDestinationFieldMapping[];
} => ({
  destination: action.destination ?? null,
  mappings: action.mappings ?? []
});
